import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Table, Spinner, Tooltip } from "flowbite-react";
import { HiOutlineInformationCircle, HiUserGroup } from "react-icons/hi";
import { PercentageBar, TooltipContent } from "../../../components";
import {
  AddExpansionOrganizational,
  EditExpansionOrganizational,
} from "./Modals";
import {
  useExpansionOrganizational,
  useDeleteConfirmation,
  useCurrency,
} from "../../../hooks";
import {
  formatCurrency,
  getFormattedMoney,
  parseMoneyValue,
} from "../../../utils";

const COLUMNS = ["Name", "Contact", "Amount", "Active On", ""];

export const ExpansionOrganizational = () => {
  const { id: accountId } = useParams();
  const [editingExpansion, setEditingExpansion] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { currency } = useCurrency();

  const {
    organizationalExpansions,
    loading,
    error,
    fetchOrganizationalExpansions,
    deleteOrganizationalExpansion,
    getSummaryStats,
  } = useExpansionOrganizational(accountId);

  useEffect(() => {
    fetchOrganizationalExpansions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { confirmDelete } = useDeleteConfirmation();
  const { totalOpportunities, totalPotentialMRR } = getSummaryStats();

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const getContactDisplay = (expansion) => {
    if (expansion.account_contact) {
      const contact = expansion.account_contact;
      const name =
        `${contact.first_name || ""} ${contact.last_name || ""}`.trim();
      return name
        ? `${name} (${contact.email || "N/A"})`
        : contact.email || "N/A";
    }
    return "N/A";
  };

  const handleEdit = (expansion) => {
    setEditingExpansion(expansion);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (expansionId) => {
    await confirmDelete(async () => {
      await deleteOrganizationalExpansion(expansionId);
    }, "Are you sure you want to delete this expansion opportunity?");
  };

  const handleRefresh = () => {
    fetchOrganizationalExpansions();
  };

  // Prepare data for percentage bar
  const CURRENT = {
    name: "Current",
    amount: 1000, // This should come from actual current MRR data
    confirmed: true,
  };

  const percentageBarData = [
    CURRENT,
    ...organizationalExpansions.map((expansion) => ({
      name: expansion.name,
      amount: parseMoneyValue(expansion.additional_mrr).value,
      // confirmed: new Date(expansion.activation_date) <= new Date(),
      // Ben says, for new leave them all unconfirmed
      confirmed: false,
    })),
  ];

  return (
    <div>
      {/* Header outside card */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <HiUserGroup className="size-5 text-blue-500" />
          <h2 className="text-base font-semibold text-gray-900">
            Expansion organisational
          </h2>
          <Tooltip
            content={
              <TooltipContent
                title="About Organisational Expansion"
                size="lg"
                text={
                  <div>
                    Use this section to register opportunities to expand into
                    new parts of the client's business. This includes:
                    <ul className="list-disc pl-5">
                      <li>New departments or subsidiaries</li>
                      <li>Expanding to other countries or group companies</li>
                    </ul>
                  </div>
                }
              />
            }
            // eslint-disable-next-line react/style-prop-object
            style="light"
          >
            <HiOutlineInformationCircle className="h-3.5 w-3.5 text-gray-400" />
          </Tooltip>
        </div>

        <AddExpansionOrganizational
          accountId={accountId}
          onExpansionAdded={handleRefresh}
        />
      </div>

      {loading && organizationalExpansions.length === 0 ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white px-5 py-8">
          <Spinner size="sm" className="mr-2" />
          <p className="text-sm text-gray-400">
            Loading organisational expansions…
          </p>
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-5 py-8">
          <p className="text-sm text-red-500">
            Error loading organisational expansions
          </p>
          <Button size="sm" onClick={handleRefresh}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
          <div className="mb-4 flex items-center gap-6">
            <div>
              <p className="text-xs text-gray-500">Opportunities</p>
              <p className="text-2xl font-bold text-gray-900">
                {totalOpportunities}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Potential MRR</p>
              <p
                className="text-2xl font-bold text-gray-900"
                title={formatCurrency(totalPotentialMRR, currency, {
                  abbreviated: false,
                })}
              >
                {formatCurrency(totalPotentialMRR, currency)}
              </p>
            </div>
          </div>

          <div className="flex w-full flex-col">
            {organizationalExpansions.length > 0 ? (
              <>
                <Table hoverable className="mb-5 drop-shadow-none">
                  <Table.Head>
                    {COLUMNS.map((column) => (
                      <Table.HeadCell key={column}>{column}</Table.HeadCell>
                    ))}
                  </Table.Head>

                  <Table.Body>
                    {organizationalExpansions.map((expansion) => {
                      return (
                        <Table.Row
                          key={expansion.id}
                          className="border-t dark:border-gray-700"
                        >
                          <Table.Cell>{expansion.name}</Table.Cell>
                          <Table.Cell>
                            {getContactDisplay(expansion)}
                          </Table.Cell>
                          <Table.Cell>
                            {getFormattedMoney(expansion.additional_mrr)}
                          </Table.Cell>
                          <Table.Cell>
                            {formatDate(expansion.activation_date)}
                          </Table.Cell>
                          <Table.Cell>
                            <div className="flex justify-end gap-2">
                              <Button
                                color="gray"
                                size="sm"
                                onClick={() => handleEdit(expansion)}
                              >
                                Edit
                              </Button>
                              <Button
                                color="failure"
                                size="sm"
                                onClick={() => handleDelete(expansion.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })}
                  </Table.Body>
                </Table>

                <PercentageBar values={percentageBarData} />
              </>
            ) : (
              <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                <HiUserGroup className="size-8 text-gray-300" />
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    No organisational expansions yet
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Add cross-team or department-level opportunities.
                  </p>
                </div>
                <AddExpansionOrganizational
                  accountId={accountId}
                  onExpansionAdded={handleRefresh}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Modal */}
      <EditExpansionOrganizational
        accountId={accountId}
        expansion={editingExpansion}
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingExpansion(null);
        }}
        onExpansionUpdated={handleRefresh}
      />
    </div>
  );
};
